import fastGlob from 'fast-glob';
import * as fs from 'fs';
import { IPackageJson } from 'package-json-type';
import * as path from 'path';
import type { ProjectConfig } from '../../../types';
import generateS3Key from './generateS3Key';
import logger from '../../../utilities/logger';
import uploadToS3 from './uploadToS3';
import zip from './zip';
import prepareWorkspaceBundle, { ZipEntry } from './workspace';

const getAppFiles = async (
  globsInput: string[] | undefined,
  appPath: string,
  appPkgJson: IPackageJson,
): Promise<ZipEntry[]> => {
  const globs = [
    '!node_modules',
    '!**/node_modules',
    '!.git',
    '!**/.git',
    '!.gitignore',
    '!**/.gitignore',
  ];

  if (globsInput && globsInput.length) {
    globs.push(
      ...globsInput,
      path.join(appPath, 'package.json'),
      path.join(appPath, 'package-lock.json'),
      path.join(appPath, 'yarn.lock'),
      path.join(appPath, 'pnpm-lock.yaml'),
      path.join(appPath, 'shrinkwrap.yaml'),
      path.join(appPath, 'bun.lockb'),
      path.join(appPath, 'bun.lock'),
    );
  } else {
    globs.push('**');
  }

  // automatically add hooks to `appFiles` if they exist
  for (const hookName of [
    'todesktop:beforeInstall',
    'todesktop:beforeBuild',
    'todesktop:afterPack',
  ]) {
    const scripts = (appPkgJson.scripts || {}) as Record<string, string>;
    const hookPath = scripts[hookName];
    if (hookPath) {
      globs.push(path.join(appPath, hookPath));
    }
  }

  const normalizedGlobs = globs
    .map((glob) => {
      // Make relative if it's not
      const globToUse = path.isAbsolute(glob)
        ? path.relative(appPath, glob)
        : glob;

      return (
        globToUse
          // Globs must always have forward slashes
          .replace(/\\/g, '/')
          // Remove trailing slash
          .replace(/\/+$/, '')
      );
    })
    .filter((glob) => !glob.startsWith('..') && !glob.startsWith('!..'));

  let absolutePaths = await fastGlob(normalizedGlobs, {
    absolute: true,
    cwd: appPath,
    dot: true,
    followSymbolicLinks: false,
    onlyDirectories: false,
    onlyFiles: true,
    globstar: true,
    unique: true,
  });

  if (process.platform === 'win32') {
    absolutePaths = absolutePaths.map((absolutePath) =>
      absolutePath.replace(/\//g, path.sep),
    );
  }

  if (!absolutePaths || !absolutePaths.length) {
    throw new Error('No files found to upload');
  } else if (
    !absolutePaths.filter(
      (absolutePath) =>
        absolutePath.endsWith('.js') || absolutePath.endsWith('.ts'),
    ).length
  ) {
    throw new Error(
      `No .js/.ts files found to upload (${absolutePaths[0]}). There's likely an issue with the appFiles option. Learn more at https://www.npmjs.com/package/@todesktop/cli#appfiles----optional-array-of-glob-patterns. If this is not the case, please contact us.`,
    );
  } else {
    let mainFilePath = appPath;
    if (appPkgJson.main) {
      mainFilePath = path.join(mainFilePath, appPkgJson.main);
    }

    if (fs.statSync(mainFilePath).isDirectory()) {
      mainFilePath = path.join(mainFilePath, 'index.js');
    }

    // Make sure the "main" file is included
    if (!absolutePaths.includes(mainFilePath)) {
      throw new Error(
        `The "main" file specified in your package.json (${
          appPkgJson.main
            ? path.relative(appPath, mainFilePath)
            : 'defaults to index.js'
        }) is not set to be uploaded to our servers. This is likely due to how you have configured the \`appFiles\` option. Learn more at https://www.npmjs.com/package/@todesktop/cli#appfiles----optional-array-of-glob-patterns. If this is not the case, please contact us.`,
      );
    }
  }

  return absolutePaths.map((absolutePath) => {
    return {
      from: absolutePath,
      to: path.join('app', path.relative(appPath, absolutePath)),
    } satisfies ZipEntry;
  });
};

export default async function uploadApplicationSource({
  appId,
  appPkgJson,
  buildId,
  config,
  onProgress,
}: {
  appId: string;
  appPkgJson: IPackageJson;
  buildId: string;
  config: ProjectConfig;
  onProgress: (progress: number, uploadedSize?: number) => void;
}) {
  logger.debug(
    {
      scrub: {
        appId,
        appPkgJson,
        buildId,
        config,
        onProgress,
      },
    },
    'uploadApplicationSource',
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let totalBytes = 0;
  const { appPath } = config;
  if (!appPath) {
    throw new Error('Project config is missing appPath');
  }

  let effectiveAppPkgJson = appPkgJson;
  let workspaceEntries: ZipEntry[] = [];

  try {
    const workspaceBundle = await prepareWorkspaceBundle({
      appPath,
      appPkgJson,
      config,
    });
    effectiveAppPkgJson = workspaceBundle.appPackageJson;
    workspaceEntries = workspaceBundle.entries;
  } catch (error) {
    logger.error({ err: error }, 'Failed to prepare workspace bundle');
    throw error;
  }

  const files: ZipEntry[] = [
    /*
      App files (stored in app/ in the ZIP)
    */
    ...(await getAppFiles(config.appFiles, appPath, effectiveAppPkgJson)),

    /*
      Optional extra content files (stored in extraContentFiles/ in the ZIP). Their
      paths within is the their relative path from the project root so
      there will be no issues with clashing filenames. We also don't
      also any file paths in our todesktop.json that are outside of the
      project directory
    */
    ...(config.extraContentFiles || []).map(
      ({ from, to = '.' }: ConfigPathEntry) => {
        return {
          from,
          to: path.join('extraContentFiles', to, path.basename(from)),
        };
      },
    ),

    /*
      Optional extra resources (stored in extraResources/ in the ZIP).
      Similar to extra content files above
    */
    ...(config.extraResources || []).map(
      ({ from, to = '.' }: ConfigPathEntry) => {
        return {
          from,
          to: path.join('extraResources', to, path.basename(from)),
        };
      },
    ),

    /*
      Icons (more may be added below)
    */
    {
      from: config.icon,
      to: path.join('icons', 'appIcon' + path.extname(config.icon)),
    },
  ];

  if (config.linux) {
    if (config.linux.icon) {
      files.push({
        from: config.linux.icon,
        to: path.join(
          'icons',
          'appIcon-linux' + path.extname(config.linux.icon),
        ),
      });
    }
  }

  if (Array.isArray(config.fileAssociations)) {
    const possibleIcons = [];
    for (const fa of config.fileAssociations) {
      if (fa.icon) {
        const icon = path.parse(fa.icon);
        // The right extension is set by electron-builder
        // https://github.com/electron-userland/electron-builder/blob/abf52eaf8dfba63eb9507c03032d8f7e2f26dd2b/packages/builder-util/src/util.ts#L320
        possibleIcons.push(
          { ext: fa.ext, icon: path.join(icon.dir, icon.name) + '.ico' },
          { ext: fa.ext, icon: path.join(icon.dir, icon.name) + '.icns' },
        );
      }
    }

    await Promise.all(
      possibleIcons.map(async ({ ext, icon }) => {
        if (await exists(icon)) {
          files.push({
            from: icon,
            to: path.join('icons', 'association-' + ext + path.extname(icon)),
          });
        }
      }),
    );
  }

  if (config.mac) {
    if (config.mac.entitlements) {
      files.push({
        from: config.mac.entitlements,
        to: path.join('other', 'mac', 'entitlements.mac.plist'),
      });
    }

    if (config.mac.entitlementsInherit) {
      files.push({
        from: config.mac.entitlementsInherit,
        to: path.join('other', 'mac', 'entitlementsInherit.mac.plist'),
      });
    }

    if (config.mac.icon) {
      files.push({
        from: config.mac.icon,
        to: path.join('icons', 'appIcon-mac' + path.extname(config.mac.icon)),
      });
    }

    if (config.mac.requirements) {
      files.push({
        from: config.mac.requirements,
        to: path.join('other', 'mac', 'requirements.txt'),
      });
    }
  }

  if (config.mas) {
    if (config.mas.entitlements) {
      files.push({
        from: config.mas.entitlements,
        to: path.join('other', 'mac', 'entitlements.mas.plist'),
      });
    }

    if (config.mas.entitlementsInherit) {
      files.push({
        from: config.mas.entitlementsInherit,
        to: path.join('other', 'mac', 'entitlementsInherit.mas.plist'),
      });
    }

    if (config.mas.provisioningProfile) {
      files.push({
        from: config.mas.provisioningProfile,
        to: path.join('other', 'mac', 'mas.provisionprofile'),
      });
    }
  }

  if (config.dmg) {
    if (config.dmg.background) {
      files.push({
        from: config.dmg.background,
        to: path.join('other', 'mac', 'dmg-background.tiff'),
      });
    }
  }

  if (config.windows) {
    if (config.windows.icon) {
      files.push({
        from: config.windows.icon,
        to: path.join(
          'icons',
          'appIcon-windows' + path.extname(config.windows.icon),
        ),
      });
    }

    if (config.windows.nsisInclude) {
      files.push({
        from: config.windows.nsisInclude,
        to: path.join('other', 'installer.nsh'),
      });
    }
  }

  if (workspaceEntries.length) {
    files.push(...workspaceEntries);
  }

  return uploadToS3({
    bucket: config.bucket || undefined,
    inputStream: await zip({
      files,
      fileSizeLimit: config.uploadSizeLimit,
      onError: (e) => {
        throw e;
      },
      onProgress({ fs: fileStats }) {
        totalBytes = fileStats.totalBytes;
      },
      appPkgJson: effectiveAppPkgJson,
    }),
    key: generateS3Key({
      appId,
      buildId,
      filenameSuffix: `${effectiveAppPkgJson.name?.replaceAll('/', '')}.zip`,
    }),
    onProgress({ loaded, total }) {
      onProgress((loaded / total) * 100, loaded);
    },
  });
}

async function exists(filePath: string): Promise<boolean> {
  try {
    await fs.promises.access(filePath);
    return true;
  } catch {
    return false;
  }
}

interface ConfigPathEntry {
  from: string;
  to?: string;
}
